import {
  KVAnnotation,
  KVMeasurement,
  KVMeasurementJSON,
  KVPlaneIntersection,
  KVPlaneIntersectionJSON,
  KVPrimitive,
  KVReferencePlane,
  KVReferencePlaneJSON,
} from 'types'
import { KVInspectionArea } from 'types/kestrelViewer/KVInspectionArea'
import {
  type KVPhotoAnnotationProjections,
  parsePhotoAnnotationProjections,
} from 'types/kestrelViewer/KVPhotoAnnotationProjection'

/** The markup stored on kestrel-model and kestrel-annotations entries. */
export type KestrelMarkupData = {
  annotations: KVAnnotation[]
  measurements: KVMeasurement[]
  primitives: KVPrimitive[]
  inspectionAreas: KVInspectionArea[]
  planeIntersections: KVPlaneIntersection[]
  referencePlanes: KVReferencePlane[]
  photoAnnotationProjections: KVPhotoAnnotationProjections
}

/** Parses Kestrel markup from raw Rolodex entry data. */
export const parseKestrelMarkup = (
  recordData: Record<string, unknown>
): KestrelMarkupData => {
  const annotations = new Array<KVAnnotation>()
  const measurements = new Array<KVMeasurement>()
  const planeIntersections = new Array<KVPlaneIntersection>()
  const inspectionAreas = new Array<KVInspectionArea>()
  const primitives = new Array<KVPrimitive>()
  const referencePlanes: KVReferencePlane[] = []

  if (recordData.annotations) {
    const annotationJSONs = recordData.annotations as Record<string, unknown>[]
    for (const annotation of annotationJSONs) {
      annotations.push(KVAnnotation.fromJSON(annotation))
    }
  }

  if (recordData.measurements) {
    const measurementJSONs = recordData.measurements as KVMeasurementJSON[]
    for (const measurement of measurementJSONs) {
      measurements.push(KVMeasurement.fromJSON(measurement))
    }
  }

  if (recordData.planeIntersections) {
    const planeIntersectionJSONs =
      recordData.planeIntersections as KVPlaneIntersectionJSON[]
    for (const planeIntersection of planeIntersectionJSONs) {
      planeIntersections.push(KVPlaneIntersection.fromJSON(planeIntersection))
    }
  }

  if (recordData.inspectionAreas) {
    const inspectionAreaJSONs = recordData.inspectionAreas as Record<
      string,
      unknown
    >[]
    for (const inspectionArea of inspectionAreaJSONs) {
      inspectionAreas.push(KVInspectionArea.fromJSON(inspectionArea))
    }
  }

  if (recordData.primitives) {
    const primitiveJSONs = recordData.primitives as Record<string, unknown>[]
    for (const primitive of primitiveJSONs) {
      primitives.push(KVPrimitive.fromJSON(primitive))
    }
  }

  if (recordData.referencePlanes) {
    const planeJSONs = recordData.referencePlanes as KVReferencePlaneJSON[]
    for (const planeJSON of planeJSONs) {
      referencePlanes.push(KVReferencePlane.fromJSON(planeJSON))
    }
  } else if (recordData.referencePlane) {
    referencePlanes.push(
      KVReferencePlane.fromJSON(
        recordData.referencePlane as KVReferencePlaneJSON
      )
    )
  }

  return {
    annotations,
    measurements,
    primitives,
    inspectionAreas,
    planeIntersections,
    referencePlanes,
    photoAnnotationProjections: parsePhotoAnnotationProjections(
      recordData.photoAnnotationProjections
    ),
  }
}
